import { useCallback, useEffect, useState } from "react"
import { RefreshCw, UserPlus } from "lucide-react"
import { toast } from "sonner"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  getReferralAttempts,
  getReferrals,
  type ReferralAttemptRow,
  type ReferralRow,
} from "@/lib/api"

function who(id: string, name: string) {
  if (!id) return "-"
  return name ? `${name} (${id})` : id
}

function notice(r: ReferralRow) {
  return r.notice_detail ? `${r.notice} (${r.notice_detail})` : r.notice
}

function Empty({ cols, text }: { cols: number; text: string }) {
  return (
    <tr>
      <td colSpan={cols} className="px-3 py-6 text-center text-muted-foreground">
        {text}
      </td>
    </tr>
  )
}

function JoinsTable({ rows }: { rows: ReferralRow[] | null }) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left">
          <tr>
            <th className="px-3 py-2">Inviter</th>
            <th className="px-3 py-2">Joined member</th>
            <th className="px-3 py-2">Code</th>
            <th className="px-3 py-2">Joined</th>
            <th className="px-3 py-2">Relationship</th>
            <th className="px-3 py-2">Notice</th>
            <th className="px-3 py-2">Report run</th>
          </tr>
        </thead>
        <tbody>
          {(rows ?? []).map((r) => (
            <tr key={`${r.code}-${r.joined_id}`} className="border-t">
              <td className="px-3 py-2">{who(r.inviter_id, r.inviter_name)}</td>
              <td className="px-3 py-2">{who(r.joined_id, r.joined_name)}</td>
              <td className="px-3 py-2 font-mono">{r.code}</td>
              <td className="px-3 py-2">{new Date(r.joined_at).toLocaleString()}</td>
              <td className="px-3 py-2">{r.relationship_state ?? "-"}</td>
              <td className="px-3 py-2">{notice(r)}</td>
              <td className="px-3 py-2 font-mono text-xs">{r.report_run_id ?? "-"}</td>
            </tr>
          ))}
          {rows !== null && rows.length === 0 && <Empty cols={7} text="No referrals yet." />}
        </tbody>
      </table>
    </div>
  )
}

function AttemptsTable({ rows }: { rows: ReferralAttemptRow[] | null }) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left">
          <tr>
            <th className="px-3 py-2">Time</th>
            <th className="px-3 py-2">Member</th>
            <th className="px-3 py-2">Code</th>
            <th className="px-3 py-2">Code owner</th>
            <th className="px-3 py-2">Identity</th>
            <th className="px-3 py-2">Outcome</th>
          </tr>
        </thead>
        <tbody>
          {(rows ?? []).map((a) => (
            <tr key={`${a.created_at}-${a.member_id}-${a.code}`} className="border-t">
              <td className="px-3 py-2">{new Date(a.created_at).toLocaleString()}</td>
              <td className="px-3 py-2">{who(a.member_id, a.member_name)}</td>
              <td className="px-3 py-2 font-mono">{a.code}</td>
              <td className="px-3 py-2">{who(a.code_owner_id, a.code_owner_name)}</td>
              <td className="px-3 py-2">{a.identity_outcome}</td>
              <td className="px-3 py-2">{a.outcome}</td>
            </tr>
          ))}
          {rows !== null && rows.length === 0 && <Empty cols={6} text="No invite codes texted yet." />}
        </tbody>
      </table>
    </div>
  )
}

function failure(reason: unknown, fallback: string) {
  return reason instanceof Error ? reason.message : fallback
}

export function ReferralsPage() {
  const [rows, setRows] = useState<ReferralRow[] | null>(null)
  const [attempts, setAttempts] = useState<ReferralAttemptRow[] | null>(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const [joins, tried] = await Promise.allSettled([getReferrals(), getReferralAttempts()])
    if (joins.status === "fulfilled") setRows(joins.value)
    else toast.error(failure(joins.reason, "Failed to load referrals"))
    if (tried.status === "fulfilled") setAttempts(tried.value)
    else toast.error(failure(tried.reason, "Failed to load referral attempts"))
    setLoading(false)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <UserPlus className="h-5 w-5" /> Referrals
        </h1>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
        >
          <RefreshCw className={loading ? "h-4 w-4 animate-spin" : "h-4 w-4"} /> Refresh
        </button>
      </div>
      <Tabs defaultValue="joins">
        <TabsList>
          <TabsTrigger value="joins">Joins</TabsTrigger>
          <TabsTrigger value="attempts">Attempts</TabsTrigger>
        </TabsList>
        <TabsContent value="joins" className="space-y-2">
          <p className="text-sm text-muted-foreground">
            The most recent 200 credited joins, with what the inviter's agent did with the notice.
          </p>
          <JoinsTable rows={rows} />
        </TabsContent>
        <TabsContent value="attempts" className="space-y-2">
          <p className="text-sm text-muted-foreground">The most recent 200 invite codes texted to Offseason.</p>
          <AttemptsTable rows={attempts} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
